import { SkillUpdateReview } from "./SkillUpdateReview";
import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useSkillSyncInProgress } from "../../hooks/useSkillSyncInProgress";
import { connectorNameWithAuth } from "../../lib/connector";
import { skillUpdateStatus } from "../../lib/skillUpdateStatus";
import { ActivityActions, ActivityMenuOpener } from "../ActivityFooter";
import { ConfirmModal } from "../ConfirmModal";
import { Dropdown } from "../Dropdown";
import { Fields, LinkField, Row, ValueField } from "../GitSourceFields";
import { HelpTooltip } from "../HelpTooltip";
import { Icon } from "../Icon";
import { LabelWithInfo } from "../LabelWithInfo";
import { ResultSummary } from "../ResultSummary";
import { SettingsCard as Card, SettingsHeading } from "../SettingsCard";
import { Text } from "../Text";
import { SkillUpdateStatus } from "../SkillUpdateStatus";

const Header = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

type Props = {
  skillId: string;
  canWrite: boolean;
  installation: {
    autoUpdate: boolean | null;
    autoUpdateRequested: boolean | null;
    updateRequestedAt: number | null;
    candidateHash: string | null;
    baselineUnavailable?: boolean | null;
    sourcePath: string;
    lastSyncedCommit: string | null;
    syncStartedAt: number | null;
    lastSyncedAt: number | null;
    lastError: string | null;
    source?: {
      checkExecutionId: string | null;
      checkRequestedAt: number | null;
      checkError: string | null;
      lastCheckedAt: number | null;
      remoteUrl: string;
      ref: string | null;
      connectedAppId: string | null;
      resolvedCommit: string | null;
      lastError: string | null;
      connectedApp?: { name: string; identifier: string } | undefined;
    };
  };
};

export function GitSourceCard({ skillId, canWrite, installation }: Props) {
  const z = useZero();
  const [confirming, setConfirming] = useState(false);
  const [confirmAutomatic, setConfirmAutomatic] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const source = installation.source;
  const canUseConnector = useHasAccess(
    "connected_app:use",
    source?.connectedAppId ?? undefined
  );
  const syncing = useSkillSyncInProgress(installation.syncStartedAt);
  if (!source) return null;

  const canResync =
    canWrite && (source.connectedAppId === null || canUseConnector);
  const commit = installation.lastSyncedCommit ?? source.resolvedCommit;
  const stale = installation.syncStartedAt !== null && !syncing;
  const status = skillUpdateStatus(installation, stale);
  const checking =
    source.checkExecutionId !== null || source.checkRequestedAt !== null;
  const updating = installation.updateRequestedAt !== null;
  const busy = checking || syncing || updating;
  const automatic = !!(
    installation.autoUpdate || installation.autoUpdateRequested
  );
  const checkLabel = automatic ? "Update now" : "Check for updates";
  const connectorLabel = source.connectedApp
    ? connectorNameWithAuth(source.connectedApp)
    : null;

  const repoWebUrl = source.remoteUrl.replace(/\.git$/, "");

  return (
    <>
      <Card>
        <Header style={{ flexWrap: "wrap" }}>
          <SettingsHeading>
            <Text variant="text-regular-md">Git source</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              {automatic
                ? "Updates automatically each day."
                : "Checks daily. Review changes before applying them."}
            </Text>
          </SettingsHeading>
          <ActivityActions>
            <SkillUpdateStatus
              installation={installation}
              onReview={
                installation.candidateHash && !automatic
                  ? () => setReviewing(true)
                  : undefined
              }
              reviewDisabled={!canResync || busy}
              checkLabel={checkLabel}
              checkDisabled={!canResync}
              onCheck={() =>
                z.mutate(
                  sharedMutators.checkSkillUpdates({
                    skillId,
                    connectedAppId: source.connectedAppId,
                  })
                )
              }
            />
            {canWrite && (automatic || canResync) && (
              <Dropdown
                align="bottom"
                minWidth={180}
                maxWidth={180}
                opener={
                  <ActivityMenuOpener aria-label="More Git actions">
                    <Icon name="more" size={16} />
                  </ActivityMenuOpener>
                }
                sections={[
                  {
                    items: [
                      {
                        type: "button",
                        key: "automatic-updates",
                        keepOpen: automatic,
                        left: <Icon name="clock" size={16} />,
                        label: (
                          <LabelWithInfo>
                            <span>Auto update</span>
                            <HelpTooltip
                              content={
                                stale
                                  ? "Resync from Git to finish the interrupted import before enabling auto update."
                                  : "Apply updates from Git daily. Enabling replaces local edits, and skill files become read-only."
                              }
                            />
                          </LabelWithInfo>
                        ),
                        toggled: automatic,
                        disabled:
                          installation.syncStartedAt !== null ||
                          (!automatic && !canResync),
                        onClick: () =>
                          automatic
                            ? z.mutate(
                                sharedMutators.configureSkillUpdates({
                                  skillId,
                                  connectedAppId: source.connectedAppId,
                                  automatic: false,
                                })
                              )
                            : setConfirmAutomatic(true),
                      },
                      ...(!automatic
                        ? [
                            {
                              type: "button" as const,
                              key: "resync",
                              left: <Icon name="refresh-cw" size={16} />,
                              label: "Resync from Git",
                              disabled: !canResync || busy,
                              onClick: () => setConfirming(true),
                            },
                          ]
                        : []),
                    ],
                  },
                ]}
              />
            )}
          </ActivityActions>
        </Header>
        {status.label === "Error" && (
          <ResultSummary
            role="alert"
            aria-label="Git sync error"
            style={{
              background: "var(--red-200)",
              borderColor: "var(--red-400)",
            }}
          >
            <Text variant="code-regular" color="var(--red-1000)">
              {status.detail}
            </Text>
          </ResultSummary>
        )}
        <Fields>
          <LinkField
            label="Repository"
            value={source.remoteUrl}
            href={repoWebUrl}
          />
          <Row>
            <ValueField
              label="Branch or tag"
              value={source.ref ?? "Default branch"}
              title={source.ref ?? undefined}
            />
            <ValueField
              label="Path"
              value={installation.sourcePath || "Repository root"}
              title={installation.sourcePath}
            />
            <ValueField
              label="Commit"
              value={commit ?? "Not synced yet"}
              title={commit ?? undefined}
            />
            {connectorLabel && (
              <ValueField
                label="Connector"
                value={connectorLabel}
                title={connectorLabel}
              />
            )}
          </Row>
        </Fields>
      </Card>
      {reviewing && (
        <SkillUpdateReview
          skillId={skillId}
          connectedAppId={source.connectedAppId}
          onClose={() => setReviewing(false)}
        />
      )}
      <ConfirmModal
        open={confirmAutomatic}
        onClose={() => setConfirmAutomatic(false)}
        title="Enable auto update?"
        description="This replaces the installed files with the current repository version, overwriting local edits. Future updates apply daily, and skill files become read-only."
        confirmLabel="Enable auto update"
        onConfirm={() => {
          z.mutate(
            sharedMutators.configureSkillUpdates({
              skillId,
              connectedAppId: source.connectedAppId,
              automatic: true,
            })
          );
          setConfirmAutomatic(false);
        }}
      />
      <ConfirmModal
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          z.mutate(
            sharedMutators.resyncSkill({
              skillId,
              connectedAppId: source.connectedAppId,
            })
          );
          setConfirming(false);
        }}
        title="Resync skill?"
        description="Replaces every skill file with the current files from the Git repository. Local edits will be overwritten."
        confirmLabel="Resync"
      />
    </>
  );
}
